import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getFeaturedMovies, getLatestMovies, movies as allMovies } from '@/mocks/movies';
import { categories } from '@/mocks/categories';

const featuredMovies = getFeaturedMovies();
const latestMovies = getLatestMovies(8);
const topRatedMovies = [...allMovies].sort((a, b) => b.rating - a.rating).slice(0, 6);

export default function Home() {
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '日韩大片在线观看',
    url: 'https://rihan-dapian.com',
    description: '想看日韩大片？这里汇聚最新、最热、最全的日韩大片资源，涵盖日本电影、韩国电影、日韩剧场版等精品内容。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://rihan-dapian.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '日韩大片在线观看平台提供哪些类型的电影？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们平台涵盖了日本电影、韩国电影、动漫剧场版以及各类日韩剧场版合集，包括文艺片、动作片、犯罪片、动画电影、恐怖片等多种类型，每日更新最新资源。',
        },
      },
      {
        '@type': 'Question',
        name: '日韩大片支持什么画质观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们的日韩大片资源均提供高清画质在线观看，部分热门影片支持超清画质，确保为用户带来最佳的观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '日韩大片资源多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '日韩大片平台每日更新最新的电影资源，第一时间为用户提供最新上映的日本和韩国电影，让您不会错过任何一部精彩大片。',
        },
      },
      {
        '@type': 'Question',
        name: '如何找到我想看的日韩电影？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过首页的热门推荐、最新上映板块浏览，也可以进入分类页面按日本电影、韩国电影、动漫剧场版等分类筛选，或者使用搜索功能精准找到您想看的日韩大片。',
        },
      },
      {
        '@type': 'Question',
        name: '日韩大片平台是否需要注册？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '日韩大片平台无需注册即可免费在线观看所有电影资源，我们致力于为用户提供便捷、无障碍的观影体验。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20movie%20theater%20atmosphere%20with%20dramatic%20film%20reel%20and%20spotlight%20effects%2C%20dark%20elegant%20background%20with%20warm%20golden%20accents%2C%20premium%20cinema%20aesthetic%2C%20shallow%20depth%20of%20field%2C%20luxurious%20atmosphere&width=1800&height=700&seq=hero-bg-home-01&orientation=landscape"
              alt="日韩大片在线观看"
              title="日韩大片 - 最新最全日韩大片合集"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full max-w-4xl mx-auto px-4 md:px-6 text-center -mt-8">
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                  日韩大片在线观看
                </h1>
                <p className="text-base md:text-lg text-white/80 mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed">
                  汇聚最新、最热、最全的<strong>日韩大片</strong>资源，涵盖日本电影、韩国电影、日韩剧场版等精品内容。高清画质，免费在线播放，每日更新。
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
                  <Link
                    to="/category/japan"
                    className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-white rounded-full font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-center"
                  >
                    立即观看
                  </Link>
                  <Link
                    to="/about"
                    className="w-full sm:w-auto px-8 py-3 bg-white/10 backdrop-blur-sm text-white border border-white/30 rounded-full font-medium hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer text-center"
                  >
                    了解更多
                  </Link>
                </div>
              </div>
            </div>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
              <span className="w-8 h-8 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-white/50 text-2xl animate-bounce"></i>
              </span>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-2xl bg-background-100 hover:bg-background-200 transition-all cursor-pointer"
                  >
                    <span className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-2xl bg-primary-100 group-hover:bg-primary-200 transition-colors">
                      <i className={`${cat.icon} text-primary-500 text-xl md:text-2xl`}></i>
                    </span>
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-900 whitespace-nowrap">
                      {cat.name}
                    </span>
                    <span className="text-xs text-foreground-500 text-center line-clamp-2">
                      {cat.description}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Movies */}
          <section id="featured" className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6 md:mb-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">热门推荐</h2>
                  <p className="text-sm text-foreground-500 mt-1">精选口碑佳作，不容错过的日韩大片</p>
                </div>
                <Link
                  to="/category/korea"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
                >
                  查看全部
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-product-shop>
                {featuredMovies.slice(0, 6).map((movie) => (
                  <MovieCard key={movie.id} movie={movie} variant="featured" />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Movies */}
          <section id="latest" className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6 md:mb-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">最新上映</h2>
                  <p className="text-sm text-foreground-500 mt-1">每日更新，第一时间追遍所有日韩大片</p>
                </div>
                <Link
                  to="/category/anime"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
                >
                  查看全部
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-product-shop>
                {latestMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          {/* Top Rated Sidebar Section */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                {/* Main content area - left */}
                <div className="lg:col-span-2">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">为何选择日韩大片？</h2>
                  <div className="prose prose-sm max-w-none">
                    <p className="text-foreground-600 leading-relaxed mb-4">
                      <strong>日韩大片</strong>在全球范围内拥有庞大的粉丝群体，日本电影以其独特的叙事风格和细腻的情感表达著称，韩国电影则以大胆的题材选择和精湛的制作水准闻名。我们的<strong>日韩大片在线观看</strong>平台致力于为广大影迷提供最优质的观影体验。
                    </p>
                    <p className="text-foreground-600 leading-relaxed mb-4">
                      从宫崎骏的经典动画到奉俊昊的奥斯卡获奖作品，从新海诚的唯美爱情故事到马东锡的硬汉动作片，<strong>日韩大片免费播放</strong>平台涵盖了所有热门类型。无论您喜欢文艺片、动作片、恐怖片还是动画电影，都能在这里找到心仪的作品。
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                      <div className="p-4 rounded-xl bg-background-50 border border-background-200/70">
                        <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                          <i className="ri-movie-line text-primary-500 text-lg"></i>
                        </span>
                        <h3 className="font-heading font-semibold text-foreground-900 mb-1">海量资源</h3>
                        <p className="text-sm text-foreground-500">上千部日本电影和韩国电影资源，涵盖所有热门类型和经典作品。</p>
                      </div>
                      <div className="p-4 rounded-xl bg-background-50 border border-background-200/70">
                        <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 mb-3">
                          <i className="ri-4k-line text-accent-500 text-lg"></i>
                        </span>
                        <h3 className="font-heading font-semibold text-foreground-900 mb-1">高清画质</h3>
                        <p className="text-sm text-foreground-500">支持高清和超清画质，为您的日韩大片观影带来极致视觉体验。</p>
                      </div>
                      <div className="p-4 rounded-xl bg-background-50 border border-background-200/70">
                        <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 mb-3">
                          <i className="ri-refresh-line text-secondary-500 text-lg"></i>
                        </span>
                        <h3 className="font-heading font-semibold text-foreground-900 mb-1">每日更新</h3>
                        <p className="text-sm text-foreground-500">最新日韩大片资源每日更新，第一时间追遍所有热门影片。</p>
                      </div>
                      <div className="p-4 rounded-xl bg-background-50 border border-background-200/70">
                        <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                          <i className="ri-user-heart-line text-primary-500 text-lg"></i>
                        </span>
                        <h3 className="font-heading font-semibold text-foreground-900 mb-1">免费观看</h3>
                        <p className="text-sm text-foreground-500">无需注册会员，所有日韩大片免费在线播放，无障碍观影体验。</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sidebar - right */}
                <div className="lg:col-span-1">
                  <div className="bg-background-50 rounded-2xl p-5 border border-background-200/70 sticky top-20">
                    <h3 className="font-heading font-semibold text-foreground-900 mb-4">高分排行</h3>
                    <div className="space-y-1">
                      {topRatedMovies.map((movie, index) => (
                        <Link
                          key={movie.id}
                          to={`/article/${movie.id}`}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <span className={`w-6 h-6 flex items-center justify-center rounded-md font-bold text-xs whitespace-nowrap ${
                            index < 3 ? 'bg-accent-500 text-white' : 'bg-background-200 text-foreground-500'
                          }`}>
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground-900 truncate">{movie.title}</p>
                            <p className="text-xs text-foreground-500">{movie.year} · {movie.director}</p>
                          </div>
                          <div className="flex items-center gap-0.5">
                            <span className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                            </span>
                            <span className="text-sm font-semibold text-accent-500">{movie.rating}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative py-16 md:py-20 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20theater%20interior%20with%20empty%20red%20seats%20and%20glowing%20screen%2C%20warm%20atmospheric%20lighting%2C%20dramatic%20cinema%20aesthetic%2C%20premium%20movie%20theater%20experience%2C%20soft%20ambient%20glow&width=1800&height=500&seq=cta-bg-home-02&orientation=landscape"
              alt="日韩大片观影体验"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>

            <div className="relative max-w-4xl mx-auto px-4 md:px-6 text-center">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4">准备好了吗？开始您的日韩大片之旅</h2>
              <p className="text-white/80 mb-8 text-base md:text-lg max-w-2xl mx-auto">
                最新<strong>日韩大片2024</strong>持续更新中，涵盖<strong>韩国大片推荐</strong>和<strong>日本大片排行榜</strong>热门内容，高清画质免费在线观看。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/japan"
                  className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-white rounded-full font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  浏览日本电影
                </Link>
                <Link
                  to="/category/korea"
                  className="w-full sm:w-auto px-8 py-3 bg-white/10 backdrop-blur-sm text-white border border-white/30 rounded-full font-medium hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  浏览韩国电影
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}